import { useState } from "react";

/**
 * useEmail hook
 * Handles submitting an email to the serverless Worker.
 * Supports optional extraData (like source/page tracking).
 */
export default function useEmail({ values, formId, extraData = {} }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);

  const { email } = values;
  const serverlessBase = import.meta.env.VITE_SERVERLESS_BASE;
  const functionName = "emailSubscribe";

  async function submitEmail(e) {
    e.preventDefault();
    setLoading(true);
    setError(false);
    setMessage("");

    // 1️⃣ Basic email validation
    if (!email || !email.length) {
      setLoading(false);
      setError(true);
      setMessage({ type: "error", text: "Oops! There was no email entered" });
      return;
    }

    try {
      const url = serverlessBase.replace(/\/$/, "") + "/" + functionName;

      // 2️⃣ Build payload including extraData
      const payload = {
        email: email.trim().toLowerCase(),
        formId,
        ...extraData, // <- this allows sending { source: currentPath } or other metadata
      };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const output = await res.json().catch(() => {
        throw new Error("Invalid JSON response from server");
      });

      // 3️⃣ Handle response status
      if (res.status >= 400) {
        setError(true);
        setMessage(output.message || "Something went wrong");
      } else {
        setError(false);
        setMessage(output.message || "Subscribed successfully!");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      setError(true);
      setMessage("Network error or server problem. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  return { error, loading, message, submitEmail };
}
